import type { ContractInputs } from "@tillix/api-contract";
import type { AutoPayEnrollmentWhere } from "@tillix/db/validators/AutoPayEnrollmentWhere";
import type { PaymentMethodWhere } from "@tillix/db/validators/PaymentMethodWhere";
import type { PaymentWhere } from "@tillix/db/validators/PaymentWhere";
import { computed } from "mobx";
import { _async, _await, applySnapshot, modelFlow } from "mobx-keystone";
import { api } from "../../../orpc/client";
import { paymentMethodDbToSnapshot } from "../../../utils/db-to-snapshot";
import { compareByDateDesc } from "../../lib/compare-by-date";
import {
  createTStore,
  createTStoreFactory,
  type TStoreInit,
} from "../../lib/models/create-t-store";
import { TStore } from "../../lib/models/t-store";
import {
  AutoPayEnrollmentModel,
  PaymentMethodModel,
  PaymentModel,
} from "./model.payment";
import { autoPayEnrollmentNS, paymentMethodNS, paymentNS } from "./ns.payment";

type PaymentMethodWhereInput = typeof PaymentMethodWhere.infer;
type PaymentWhereInput = typeof PaymentWhere.infer;
type AutoPayEnrollmentWhereInput = typeof AutoPayEnrollmentWhere.infer;
type PostPaymentMethodInput = ContractInputs["payment"]["method"]["post"];
type VerifyPaymentMethodInput = ContractInputs["payment"]["method"]["verify"];

@paymentMethodNS.store
export class PaymentMethodStore extends TStore<
  PaymentMethodModel,
  PaymentMethodWhereInput,
  typeof api.payment.method
> {
  Model = PaymentMethodModel;
  api: typeof api.payment.method = api.payment.method;

  @modelFlow
  editPaymentMethod = _async(function* _editPaymentMethod(
    this: PaymentMethodStore,
    input: ContractInputs["payment"]["method"]["put"],
  ) {
    try {
      const model = this.byId(input.id, true);
      if (!model) {
        throw new Error("Payment method not in store");
      }
      const updated = yield* _await(this.api.put(input));
      const snapshot = paymentMethodDbToSnapshot(updated);

      applySnapshot(model, snapshot);
      return model;
    } catch (error) {
      console.error("Error editing payment method", error);
      throw error;
    }
  });

  @modelFlow
  deletePaymentMethod = _async(function* _deletePaymentMethod(
    this: PaymentMethodStore,
    id: string,
  ) {
    try {
      const result = yield* _await(this.api.delete({ id }));

      if (result.success) {
        const model = this.byId(id, true);
        if (model) {
          this.removeData(model);
        }
      }

      return result;
    } catch (error) {
      console.error("Error deleting payment method", error);
      throw error;
    }
  });

  /** Re-enters a bank's details so a method waiting for verification can be used. */
  @modelFlow
  verifyPaymentMethod = _async(function* _verifyPaymentMethod(
    this: PaymentMethodStore,
    input: VerifyPaymentMethodInput,
  ) {
    const verified = yield* _await(this.api.verify(input));
    const snapshot = paymentMethodDbToSnapshot(verified);
    const existing = this.byId(verified.id, true);
    if (existing) {
      applySnapshot(existing, snapshot);
    } else {
      this.addRaw([snapshot]);
    }
    return this.byId(verified.id, true);
  });

  @modelFlow
  addPaymentMethod = _async(function* _addPaymentMethod(
    this: PaymentMethodStore,
    input: PostPaymentMethodInput,
  ) {
    let newMethod: Awaited<ReturnType<typeof this.api.post>> | null = null;
    if (input.type === "CARD") {
      newMethod = yield* _await(this.addCard(input));
    }
    if (input.type === "BANK_ACCOUNT") {
      newMethod = yield* _await(this.addACH(input));
    }
    if (!newMethod) {
      throw new Error(
        `Unable to add payment method with input: ${JSON.stringify(input)}`,
      );
    }
    const snapshot = paymentMethodDbToSnapshot(newMethod);
    const existing = this.byId(newMethod.id, true);
    if (existing) {
      applySnapshot(existing, snapshot);
    } else {
      this.addRaw([snapshot]);
    }
    return this.byId(newMethod.id, true);
  });

  private async addCard(
    input: Extract<PostPaymentMethodInput, { type: "CARD" }>,
  ) {
    try {
      return await this.api.post(input);
    } catch (error) {
      console.error("Error adding card", error);
      throw error;
    }
  }

  private async addACH(
    input: Extract<PostPaymentMethodInput, { type: "BANK_ACCOUNT" }>,
  ) {
    try {
      return await this.api.post(input);
    } catch (error) {
      console.error("Error adding bank account", error);
      throw error;
    }
  }
}

@paymentNS.store
export class PaymentStore extends TStore<
  PaymentModel,
  PaymentWhereInput,
  typeof api.payment
> {
  Model = PaymentModel;
  api: typeof api.payment = api.payment;

  @computed
  get latestPayment(): PaymentModel | undefined {
    const rows = this.data;
    if (rows.length === 0) {
      // biome-ignore lint/nursery/noUselessUndefined: MobX getter needs explicit
      return undefined;
    }
    return rows.toSorted((a, b) => compareByDateDesc(a.paidAt, b.paidAt))[0];
  }
}

@autoPayEnrollmentNS.store
export class AutoPayEnrollmentStore extends TStore<
  AutoPayEnrollmentModel,
  AutoPayEnrollmentWhereInput,
  typeof api.payment.autopay
> {
  Model = AutoPayEnrollmentModel;
  api = api.payment.autopay;
  // One enrollment per service account; load them all like ServiceAccountStore,
  // or the autopay banner lists enrolled accounts as not enrolled.
  loadAllPages = true;
}

export const createPaymentMethodStore = createTStoreFactory<
  PaymentMethodModel,
  PaymentMethodStore
>(PaymentMethodStore);

export const createPaymentStore = (
  incomingData?: TStoreInit<PaymentModel, PaymentStore>,
) => createTStore<PaymentModel, PaymentStore>(PaymentStore, incomingData);

export const createAutoPayEnrollmentStore = (
  incomingData?: TStoreInit<AutoPayEnrollmentModel, AutoPayEnrollmentStore>,
) =>
  createTStore<AutoPayEnrollmentModel, AutoPayEnrollmentStore>(
    AutoPayEnrollmentStore,
    incomingData,
  );
